import { useState } from 'react';
import {
  Box, Button, Card, CardContent, IconButton, LinearProgress, Stack, TextField, Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuthStore } from '@/store/auth.store';
import { useChangePassword } from '@/features/auth/changePassword';
import { useLogout } from '@/features/auth/queries';

function strength(pwd: string) {
  let s = 0;
  if (pwd.length >= 10) s++;
  if (pwd.length >= 14) s++;
  if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) s++;
  if (/[0-9]/.test(pwd)) s++;
  if (/[^A-Za-z0-9]/.test(pwd)) s++;
  if (s <= 2) return { score: s * 20, label: 'Weak', color: 'error' as const };
  if (s <= 3) return { score: s * 20, label: 'OK', color: 'warning' as const };
  return { score: 100, label: 'Strong', color: 'success' as const };
}

/**
 * Full-screen blocker shown when the user logs in with MustChangePassword=true.
 * They cannot use the app until they change it. The only escape is signing out.
 */
export function ForcePasswordChangeGate({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const change = useChangePassword();
  const logout = useLogout();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showNext, setShowNext] = useState(false);

  if (!user?.mustChangePassword) return <>{children}</>;

  const s = strength(next);
  const mismatch = confirm.length > 0 && next !== confirm;
  const valid = current.length > 0 && next.length >= 10 && !mismatch && s.color !== 'error';

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!valid) return;
    await change.mutateAsync({ currentPassword: current, newPassword: next });
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'grid',
      placeItems: 'center',
      bgcolor: 'background.default',
      p: 2,
      background:
        'radial-gradient(at 30% 20%, rgba(200,163,90,.08), transparent 60%), radial-gradient(at 70% 80%, rgba(15,23,42,.05), transparent 60%)',
    }}>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
        <Card sx={{ width: 460, maxWidth: '100%', p: 1 }}>
          <CardContent>
            <Stack spacing={2.5} sx={{ p: 1 }}>
              <Box>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5 }}>Set a new password</Typography>
                <Typography variant="body2" color="text.secondary">
                  Your administrator created your account or reset your password.
                  Choose a new one to continue.
                </Typography>
              </Box>

              <Box component="form" onSubmit={submit}>
                <Stack spacing={2}>
                  <TextField
                    label="Current (temporary) password"
                    type="password"
                    value={current}
                    onChange={(e) => setCurrent(e.target.value)}
                    autoFocus
                    autoComplete="current-password"
                  />
                  <Box>
                    <TextField
                      label="New password"
                      type={showNext ? 'text' : 'password'}
                      value={next}
                      onChange={(e) => setNext(e.target.value)}
                      fullWidth
                      autoComplete="new-password"
                      InputProps={{
                        endAdornment: (
                          <IconButton size="small" onClick={() => setShowNext((v) => !v)}>
                            {showNext ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        ),
                      }}
                      helperText="At least 10 characters, mixed case, with a digit or symbol"
                    />
                    {next.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <LinearProgress variant="determinate" value={s.score} color={s.color} sx={{ borderRadius: 1, height: 6 }} />
                        <Typography variant="caption" color={`${s.color}.main`} sx={{ mt: 0.5, display: 'block' }}>{s.label}</Typography>
                      </Box>
                    )}
                  </Box>
                  <TextField
                    label="Confirm new password"
                    type={showNext ? 'text' : 'password'}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    autoComplete="new-password"
                    error={mismatch}
                    helperText={mismatch ? "Passwords don't match" : ''}
                  />
                  <Stack direction="row" spacing={1}>
                    <Button type="submit" size="large" sx={{ flex: 1, height: 44 }} disabled={!valid || change.isPending}>
                      {change.isPending ? 'Setting password…' : 'Set password'}
                    </Button>
                    <Button variant="text" onClick={() => logout.mutate()} disabled={change.isPending}>
                      Sign out
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </motion.div>
    </Box>
  );
}
